import { useEffect, useState } from 'react';
import ReportMetadata from '../shared/ReportMetadata';
import ReportTable from '../shared/ReportTable';
import { Tab, Tabs } from '@dsi/react-eds';
import {
  CorrectedLimitationsColumns,
  KllCorrectedLimitationsColumns,
  KllKnownLimitationsColumns,
  CorrectedLimitationsFilters,
  KllKnownLimitationsFilters,
  KllCorrectedLimitationsFilters,
  CorrectedLimitationsDialogMetadata,
  CorrectedLimitationsDialogImpactAssessment,
  KllKnownLimitationsDialogMetadata,
  KllKnownLimitationsDialogImpactAssessment,
  KllCorrectedLimitationsDialogMetadata,
  KllCorrectedLimitationsDialogImpactAssessment
} from './types/TroubleReport.types';
import { NiiReportContentResponse, ReportsType } from '../shared/types/shared.types';
import { useSwobNotifications } from '../../../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import { niiService } from '../../../../services/niiService';
import { useLoader } from '../../../../hooks/useLoader';
import { LOADER_DEBOUNCE_MS } from '../../../../const/global-constants';

interface PropsObject {
  reportId: string;
}

function TroubleReportAssessment(props: Readonly<PropsObject>) {
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  const [report, setReport] = useState<NiiReportContentResponse>();
  const [reportTab, setReportTab] = useState<string>();

  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (props.reportId) {
      getFiltersNeeded();
    }
  }, []);
  const fetchReport = async (filters?: string) => {
    try {
      showLoader();
      const response = await niiService.getReportContent(props.reportId, filters || 'page=1&limit=10&tab=nir');
      setReport(response.data);
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError ? error.response?.data?.message || error.message : 'An error occurred fetching report',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => hideLoader(), LOADER_DEBOUNCE_MS);
    }
  };

  const getFiltersNeeded = (tab?: number) => {
    const t = tab ?? selectedTab;
    let filter = 'page=1&limit=10';
    let arr: string[] = [];
    if (t === 0) {
      setReportTab('&tab=nir');
      filter = `${filter}&tab=nir`;
      arr = CorrectedLimitationsFilters.map((f) => f.attribute);
    } else if (t === 1) {
      setReportTab('&tab=known');
      filter = `${filter}&tab=known`;
      arr = KllKnownLimitationsFilters.map((f) => f.attribute);
    } else if (t === 2) {
      setReportTab('&tab=corrected');
      filter = `${filter}&tab=corrected`;
      arr = KllCorrectedLimitationsFilters.map((f) => f.attribute);
    }
    fetchReport(filter + '&filterList=' + arr.join(','));
  };

  const handleTabChange = (_: React.SyntheticEvent, activeTab: number) => {
    setReport(undefined);
    getFiltersNeeded(activeTab);
    setSelectedTab(activeTab);
  };

  //`Doc: ${metadata.doc_number} | Rev: ${metadata.revision} | Date: ${metadata.date} | Generated: `
  const getMetadataString = (): string => {
    return `Doc: ${report?.documentMetadata?.doc_number} | Rev: ${report?.documentMetadata?.revision} | Date: ${report?.documentMetadata?.date} | Generated: ${report?.documentMetadata?.date}`;
  };

  return (
    <>
      {report && (
        <div className="container">
          <ReportMetadata title={'Trouble Report Assessment'} metadata={getMetadataString()} />
          <br />
          <Tabs activeTab={selectedTab} onTabChange={handleTabChange}>
            <Tab title="NIR Trouble Reports" selected>
              {selectedTab === 0 && report ? (
                <ReportTable
                  type={ReportsType.TROUBLE}
                  data={report?.data || []}
                  reportId={props.reportId}
                  reportTab={reportTab}
                  report={report}
                  filters={CorrectedLimitationsFilters}
                  columns={CorrectedLimitationsColumns}
                  metadataFields={CorrectedLimitationsDialogMetadata}
                  impactAssessmentFields={CorrectedLimitationsDialogImpactAssessment}
                  clickable={true}
                />
              ) : (
                <></>
              )}
            </Tab>
            <Tab title="Known Limitations">
              {selectedTab === 1 && report ? (
                <ReportTable
                  type={ReportsType.KNOWN_LIMITATIONS}
                  data={report?.data || []}
                  reportId={props.reportId}
                  reportTab={reportTab}
                  report={report}
                  filters={KllKnownLimitationsFilters}
                  columns={KllKnownLimitationsColumns}
                  metadataFields={KllKnownLimitationsDialogMetadata}
                  impactAssessmentFields={KllKnownLimitationsDialogImpactAssessment}
                  clickable={true}
                />
              ) : (
                <></>
              )}
            </Tab>
            <Tab title="Corrected Limitations">
              {selectedTab === 2 && report ? (
                <ReportTable
                  type={ReportsType.CORRECTED_LIMITATIONS}
                  data={report?.data || []}
                  reportId={props.reportId}
                  reportTab={reportTab}
                  report={report}
                  filters={KllCorrectedLimitationsFilters}
                  columns={KllCorrectedLimitationsColumns}
                  metadataFields={KllCorrectedLimitationsDialogMetadata}
                  impactAssessmentFields={KllCorrectedLimitationsDialogImpactAssessment}
                  clickable={true}
                />
              ) : (
                <></>
              )}
            </Tab>
          </Tabs>
        </div>
      )}
    </>
  );
}
export default TroubleReportAssessment;
